import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import Reveal from './Reveal';

const faqs = [
  {
    q: 'How do you choose your tutors?',
    a: 'Roughly one in eight applicants makes it through. Every tutor is at (or recently graduated from) a UK university, sits a subject test, teaches a mock lesson to our team, and is DBS checked before they ever meet a student.',
  },
  {
    q: 'Which subjects and levels do you cover?',
    a: 'Over 30 subjects from KS2 through GCSE and A Level, across AQA, Edexcel, OCR and WJEC. If we can’t cover something well, we’ll tell you rather than take the booking.',
  },
  {
    q: 'What happens in the first lesson?',
    a: 'The intro lesson is free. The tutor diagnoses where the gaps actually are, agrees a target with the student, and writes up a plan you can read the same evening.',
  },
  {
    q: 'Can I change tutor if it isn’t working?',
    a: 'Yes, at any point and at no cost. Fit matters more than credentials — tell us what didn’t work and we’ll rematch, usually within 24 hours.',
  },
  {
    q: 'How does pricing work?',
    a: 'You pay per lesson, with no subscription and no joining fee. Rates depend on the tutor’s level and subject, and are shown up front before you book.',
  },
];

const Faq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="section-shell py-20 sm:py-28">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow">FAQ</p>
          <h2 className="display mt-3 text-4xl text-balance sm:text-5xl">
            Questions parents
            {' '}
            <span className="italic text-brand-600 dark:text-brand-400">actually ask</span>
          </h2>
          <p className="mt-5 text-base-content/60">
            Still unsure? Talk to a real person — we answer within a working day.
          </p>
        </Reveal>

        <div className="lg:col-span-8">
          <div className="divide-y divide-ink-900/[0.08] border-y border-ink-900/[0.08] dark:divide-white/10 dark:border-white/10">
            {faqs.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={item.q}>
                  <h3>
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-6 py-5 text-left transition hover:text-brand-600 dark:hover:text-brand-400"
                    >
                      <span className="text-[15px] font-semibold sm:text-base">{item.q}</span>
                      <Plus
                        className={`h-4 w-4 shrink-0 text-base-content/40 transition-transform duration-300 ${
                          isOpen ? 'rotate-45' : ''
                        }`}
                      />
                    </button>
                  </h3>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="max-w-2xl pb-6 pr-8 text-[15px] leading-relaxed text-base-content/60">
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faq;
